/* ============================================================================
   MailBakes — product flow revisions
   assets/mb-flow-revisions.js

   An additive layer over sections/mb-flow.liquid (70KB) and assets/mb-flow.js
   (58KB). Neither is edited. Every item can be switched off in CONFIG.

     1  compact   Each step fits one desktop screen without scrolling
     2  fud       "How much cake?" guarantees run horizontally
     3  message   Card preview becomes the hero; controls collapse to a rail
     4  delivery  "When should it land?" and "How should it travel?" split in two
     5  extras    Optional extras grouped into categories, each a carousel

   HOW IT HOOKS IN — mb-flow.js gives you almost nothing
   ----------------------------------------------------
   It is a closed IIFE. Nothing on window; cur, S, step(), validate() are all
   private. It dispatches no events. So:

     * State is re-read from the DOM, never from mb-flow.js.
     * Step changes are observed via the `is-on` class on .mb-screen.
     * Flow actions are intercepted with a CAPTURE-phase listener on document.
       mb-flow.js delegates clicks on .mb-flow in the BUBBLE phase, so a
       capture listener always runs first and can veto with stopPropagation().

   THREE THINGS THAT WILL BREAK IF YOU CHANGE THEM
   -----------------------------------------------
   * NEVER reparent .mb-dblk--travel. mb-delivery.js mounts its postage picker
     into the first direct child div of step 5's .mb-wrap. Move it and the
     picker lands under the wrong heading. The split is done with display:none
     only.
   * NEVER inject into #mb-gifts, #mb-stage, #mb-fonts, #mb-cols, #mb-dates or
     #mb-calwrap and expect it to survive — mb-flow.js does innerHTML = on all
     of them. #mb-gifts is rebuilt on EVERY extra tap, which is why item 5
     needs a MutationObserver.
   * NEVER clone #mb-blank. Its change listener is bound directly to the node,
     so a copy is dead. appendChild moves it and the listener travels with it.
   ============================================================================ */

(function () {
  'use strict';

  var CONFIG = {
    compact:  true,
    fud:      true,
    message:  true,
    delivery: false,
    extras:   true,

    /* Aug 2026 revisions 2 */
    fit:      true,   /*  8  "How much cake?" fits an ordinary laptop window   */
    hero:     true,   /*  9  message preview is the hero, controls collapse    */
    calendar: true,   /* 10  the date picker opens where you can see it        */

    /* No category data exists — the flow serialises extras as
       {id,title,price,image,image_big} from two products' variants, with no
       grouping field. So the map lives here. First match wins; anything
       unmatched falls into OTHER, which renders last. */
    CATEGORIES: [
      { name: 'Hot chocolate spoons', test: /hot chocolate spoon/i },
      { name: 'Drinks',               test: /tea|coffee|nescaf|hot chocolate|brew/i },
      { name: 'Balloons',             test: /balloon/i },
      { name: 'Sweets',               test: /pick n mix|sweet|candy|chocolate bar/i },
      { name: 'Finishing touches',    test: /envelope|candle|card|topper|ribbon/i }
    ],
    OTHER: 'More treats'
  };

  var root = document.querySelector('[data-mb-flow]');
  if (!root) return;

  var $  = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) { return [].slice.call((c || document).querySelectorAll(s)); };

  /* ---------- styles ------------------------------------------------------
     Injected at runtime so it lands after mb-flow3.css. The two stylesheets
     mb-flow.js and mb-delivery.js inject themselves (#mb-pv-css, #mb-ship-css)
     may land after this one, so anything touching .mb-pv* or .mb-ship* is
     written as a descendant of .mb-flow to out-specify them. */
  function styles() {
    if ($('#mb-fr-css')) return;
    var css = document.createElement('style');
    css.id = 'mb-fr-css';
    var out = [];

    if (CONFIG.fud) out.push(
      '@media(min-width:1101px){',
      '.mb-flow .mb-fud{flex-direction:row;gap:8px;margin-top:14px}',
      '.mb-flow .mb-fud__i{flex:1 1 0;min-width:0;flex-direction:column;text-align:center;',
        'gap:8px;padding:12px 10px}',
      '.mb-flow .mb-fud__ic{width:38px;height:38px}',
      '.mb-flow .mb-fud__i b{font-size:13px}',
      '.mb-flow .mb-fud__i small{font-size:11.5px}',
      '}');

    /* ---- item 8 · "How much cake?" on one screen ------------------------
       The compact block below only engages at 1101px, which is a large
       desktop. Measured on a perfectly ordinary 814x672 window, step 2 was
       1692px tall — two and a half screens — because .mb-two was still a
       single column and .mb-gal__vp is a SQUARE, so the card preview alone
       took 778px of a 672px window.

       Three things do the work, in order of how much they save:
         two columns from 780px up          -1692 -> ~max(col) instead of sum
         the preview capped against vh      -778  -> ~44vh
         the guarantees on one row          -224  -> ~one row
       Everything is a cap or a breakpoint, so nothing is squeezed on a phone,
       where a single column and a full-width preview are still right. */
    if (CONFIG.fit) out.push(
      /* Cap the preview against the WINDOW, not the column. object-fit keeps
         the card's own proportions inside whatever height is left. */
      '.mb-flow .mb-screen[data-step="2"] .mb-gal__vp{max-height:min(44vh,420px)}',
      '.mb-flow .mb-screen[data-step="2"] .mb-gal__vp img{height:100%;width:100%;object-fit:contain}',

      /* Two columns well below the old 1101px. 780 is the point at which each
         column is still wide enough for the slice buttons to read on one line. */
      '@media(min-width:780px){',
      '.mb-flow .mb-screen[data-step="2"] .mb-two{grid-template-columns:minmax(0,1fr) minmax(0,1fr);',
        'gap:26px;align-items:start}',
      '.mb-flow .mb-screen[data-step="2"] .mb-opts .mb-opt{padding-top:11px;padding-bottom:11px}',
      '}',

      /* The objection qualifiers: three stacked 68px rows became one row.
         Same words, a third of the height. */
      '@media(min-width:700px){',
      '.mb-flow .mb-screen[data-step="2"] .mb-fud{flex-direction:row;gap:8px;margin-top:12px}',
      '.mb-flow .mb-screen[data-step="2"] .mb-fud__i{flex:1 1 0;min-width:0;flex-direction:column;',
        'text-align:center;gap:6px;padding:10px 8px}',
      '.mb-flow .mb-screen[data-step="2"] .mb-fud__ic{width:30px;height:30px;flex:0 0 30px}',
      '.mb-flow .mb-screen[data-step="2"] .mb-fud__i b{font-size:12.5px;line-height:1.25}',
      '.mb-flow .mb-screen[data-step="2"] .mb-fud__i small{font-size:11px;line-height:1.3}',
      '}',

      /* The review quote under the qualifiers — kept, because it is doing a
         job, just not at 117px. */
      '.mb-flow .mb-screen[data-step="2"] .mb-pdprev{margin-top:12px}',
      '.mb-flow .mb-screen[data-step="2"] .mb-pdprev blockquote{font-size:13px;line-height:1.35}',
      '.mb-flow .mb-screen[data-step="2"] .mb-pdprev figcaption{font-size:11.5px}',

      /* Only when the window is genuinely short is it worth trimming the
         chrome as well — on a tall screen this would just look mean. */
      '@media(min-width:780px) and (max-height:820px){',
      '.mb-flow .mb-screen{padding:12px 0 8px}',
      '.mb-flow .mb-fhead{margin-bottom:10px}',
      '.mb-flow .mb-screen[data-step="2"] .mb-baked{margin:8px 0}',
      '}',

      /* Last of the height, taken from spacing rather than from anything the
         customer reads. The review quote stays: on a step whose whole job is
         answering "is this actually any good", deleting the proof to save
         120px would be a strange trade. */
      '@media(min-width:780px){',
      '.mb-flow .mb-screen[data-step="2"] .mb-opts{gap:8px}',
      '.mb-flow .mb-screen[data-step="2"] .mb-opts .mb-opt__p{line-height:1.15}',
      '.mb-flow .mb-screen[data-step="2"] .mb-gal__dots{margin-top:8px}',
      '.mb-flow .mb-screen[data-step="2"] .mb-des__n{font-size:19px;margin-bottom:2px}',
      '}');

    if (CONFIG.compact) out.push(
      /* Height only matters when there is height to fit into — a short laptop
         window is better off scrolling than squeezed. */
      '@media(min-width:1101px) and (min-height:720px){',
      '.mb-flow{min-height:0}',
      '.mb-flow .mb-screen{padding:16px 0 8px}',
      '.mb-flow .mb-fhead{margin-bottom:14px}',
      '.mb-flow .mb-fhead h1,.mb-flow .mb-fhead h2{font-size:26px;margin-bottom:4px}',
      '.mb-flow .mb-two{gap:28px}',
      '.mb-flow .mb-stage{min-height:0;padding:16px}',
      '.mb-flow .mb-flav{min-height:56px}',
      '}');

    if (CONFIG.message) out.push(
      /* Preview as hero: give the stage the lion's share and stand the
         controls up as a narrow rail beside it. The <details> folds are
         already collapsible, so they become the rail buttons as-is. */
      '@media(min-width:1101px){',
      '.mb-flow .mb-screen[data-step="4"] .mb-two{grid-template-columns:minmax(0,1.55fr) minmax(0,1fr)}',
      '.mb-flow .mb-screen[data-step="4"] .mb-style2{grid-template-columns:1fr;gap:10px;margin-top:12px}',
      '.mb-flow .mb-screen[data-step="4"] .mb-fold{margin-top:0}',
      '.mb-flow .mb-screen[data-step="4"] .mb-stage{min-height:0}',
      '}',
      /* The rail groups Font, Colour, Leave blank and the helper into one
         column so they read as four peers rather than two folds and two
         strays. .mb-blank and .mb-helpbtn are MOVED into it, not copied. */
      '.mb-flow .mbfr-rail{display:grid;gap:10px;margin-top:12px}',
      '.mb-flow .mbfr-rail .mb-blank{margin:0;background:#fff;border:1px solid var(--mb-line,#EDE6E9);',
        'border-radius:12px;padding:13px 15px}',
      '.mb-flow .mbfr-rail .mb-helpbtn{width:100%;justify-content:flex-start}');

    /* ---- item 9 · the message preview becomes the hero -------------------
       Same intent as CONFIG.message above, brought down from 1101px to the
       width where two columns actually start to fit, plus the two things the
       brief asks for that the earlier pass did not do: a preview sized against
       the window rather than its contents, and the words centred inside it. */
    if (CONFIG.hero) out.push(
      '@media(min-width:780px){',
      '.mb-flow .mb-screen[data-step="4"] .mb-two{grid-template-columns:minmax(0,1.5fr) minmax(0,1fr);',
        'gap:26px;align-items:start}',
      '.mb-flow .mb-screen[data-step="4"] .mb-style2{grid-template-columns:1fr;gap:10px;margin-top:12px}',
      '.mb-flow .mb-screen[data-step="4"] .mb-fold{margin-top:0}',
      '}',

      /* The preview earns the height here — it is the thing the customer came
         to this step to look at. Capped against the window so it never pushes
         the Continue button off a short screen. */
      '.mb-flow .mb-screen[data-step="4"] .mb-stage{min-height:min(52vh,460px);',
        'display:grid;place-items:center}',
      '.mb-flow .mb-screen[data-step="4"] .mb-stage .mb-pv{width:100%}',

      /* "Centre the message within the preview." Applied to the preview only.
         NOTE for whoever prints these: the step says "Printed exactly as
         shown", so the card itself has to be centred too or the promise on
         screen stops being true. */
      '.mb-flow .mb-screen[data-step="4"] .mb-pv__t{text-align:center}',

      /* The message box is a supporting actor now, not the main event. */
      '@media(min-width:780px){',
      '.mb-flow .mb-screen[data-step="4"] .mb-msg{min-height:120px}',
      '}');

    if (CONFIG.delivery) out.push(
      /* The split is display only. Reparenting would move .mb-dblk--travel out
         of the first-direct-child position that mb-delivery.js mounts into. */
      '.mb-flow .mb-dwrap[data-mbfr-sub="when"] .mb-dblk--travel{display:none}',
      '.mb-flow .mb-dwrap[data-mbfr-sub="travel"] .mb-dblk--when{display:none}',
      '.mb-flow .mb-dwrap[data-mbfr-sub] .mb-dblk--travel{margin-top:0;padding-top:0;border-top:0}');

    if (CONFIG.extras) out.push(
      '.mb-flow #mb-gifts[data-mbfr-grouped]{display:block}',
      '.mb-flow .mbfr-cat{margin:0 0 18px}',
      '.mb-flow .mbfr-cat__h{font-size:15px;font-weight:750;color:var(--mb-ink,#1E1A1C);',
        'margin:0 0 10px;letter-spacing:-.01em}',
      '.mb-flow .mbfr-rail-x{display:flex;gap:14px;overflow-x:auto;scroll-snap-type:x proximity;',
        'scrollbar-width:none;-webkit-overflow-scrolling:touch;padding-bottom:4px;',
        '-webkit-mask-image:linear-gradient(90deg,#000 calc(100% - 34px),transparent);',
        'mask-image:linear-gradient(90deg,#000 calc(100% - 34px),transparent)}',
      '.mb-flow .mbfr-rail-x::-webkit-scrollbar{display:none}',
      '.mb-flow .mbfr-rail-x .mb-gift{flex:0 0 200px;scroll-snap-align:start}',
      '@media(max-width:749px){.mb-flow .mbfr-rail-x .mb-gift{flex:0 0 150px}}');

    css.textContent = out.join('');
    document.head.appendChild(css);
  }

  /* ---------- 3 · message controls into one rail --------------------------
     One-shot move. Both nodes are static Liquid that mb-flow.js never
     re-renders, and both keep working after the move: #mb-blank's listener is
     bound to the node itself, and the helper button's listener is on document. */
  function messageRail() {
    if (document.querySelector(".mb-message-simple")) return;
    var folds = $('.mb-screen[data-step="4"] .mb-style2');
    if (!folds || $('.mbfr-rail')) return;
    var blank = $('.mb-screen[data-step="4"] .mb-blank');
    var help  = $('.mb-screen[data-step="4"] .mb-helpbtn');
    if (!blank && !help) return;

    var rail = document.createElement('div');
    rail.className = 'mbfr-rail';
    folds.parentNode.insertBefore(rail, folds.nextSibling);
    if (blank) rail.appendChild(blank);   // move, never clone
    if (help)  rail.appendChild(help);
  }

  /* ---------- 4 · split the delivery step --------------------------------- */

  var sub = 'when', lastStep = null;

  function screen(n) { return $('.mb-screen[data-step="' + n + '"]'); }
  function onStep(n) { var s = screen(n); return !!s && s.classList.contains('is-on'); }
  function currentStep() {
    var on = $('.mb-screen.is-on');
    return on ? +on.getAttribute('data-step') : null;
  }

  function setSub(next) {
    sub = next;
    var wrap = $('.mb-screen[data-step="5"] .mb-dwrap');
    if (wrap) wrap.setAttribute('data-mbfr-sub', next);
    /* mb-flow.js only scrolls on a real step change, so a sub-step move would
       otherwise leave the customer mid-page. */
    try {
      var top = $('.mb-screen[data-step="5"]').getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: Math.max(0, top - 120), behavior: 'smooth' });
    } catch (e) {}
  }

  /* Re-implements the one rule validate() applies on step 5:
       S.when === 'pick' && !S.date  ->  "Choose a date from the calendar."
     Both halves are observable in the DOM — renderWhen() marks the chosen
     option .is-on, renderCal() marks the chosen day .is-on. */
  function whenIsValid() {
    var pick = $('#mb-dates .is-on[data-when="pick"]');
    if (!pick) return true;
    return !!$('#mb-calwrap .is-on[data-day], #mb-calwrap button.is-on');
  }

  function showErr(msg) {
    var el = $('#mb-err');
    if (el) el.textContent = msg;
  }

  function watchSteps() {
    var host = $('.mb-flow');
    if (!host) return;
    /* mb-flow.js only ever toggles `is-on` on .mb-screen, so watching the class
       attribute is cheap and cannot loop. */
    var obs = new MutationObserver(function () {
      var now = currentStep();
      if (now === lastStep) return;

      if (now === 5) {
        /* Arriving forward from 4 starts on "when"; arriving back from 6 must
           land on "travel", or Back would appear to skip a screen. */
        setSub(lastStep === 6 ? 'travel' : 'when');
      }
      lastStep = now;
    });
    $$('.mb-screen').forEach(function (s) {
      obs.observe(s, { attributes: true, attributeFilter: ['class'] });
    });
    lastStep = currentStep();
    if (lastStep === 5) setSub('when');
  }

  /* Capture phase on document — runs before mb-flow.js's delegated listener on
     .mb-flow, so stopPropagation() vetoes the step change entirely. */
  function interceptNav(e) {
    if (!CONFIG.delivery) return;

    var next = e.target.closest && e.target.closest('#mb-next');
    var back = e.target.closest && e.target.closest('#mb-back');
    if (!next && !back) return;

    if (next && onStep(5) && sub === 'when') {
      e.preventDefault();
      e.stopPropagation();
      if (!whenIsValid()) { showErr('Choose a date from the calendar.'); return; }
      showErr('');
      setSub('travel');
      return;
    }

    if (back && onStep(5) && sub === 'travel') {
      e.preventDefault();
      e.stopPropagation();
      showErr('');
      setSub('when');
      return;
    }

    /* Going back from extras into delivery should land on the travel half,
       which is the screen they last saw. Set it before mb-flow.js moves. */
    if (back && onStep(6)) sub = 'travel';
  }

  /* ---------- 5 · extras into categorised carousels ----------------------- */

  function categoryOf(title) {
    for (var i = 0; i < CONFIG.CATEGORIES.length; i++) {
      if (CONFIG.CATEGORIES[i].test.test(title)) return CONFIG.CATEGORIES[i].name;
    }
    return CONFIG.OTHER;
  }

  var expandedExtras = {};
  function regroup(gifts) {
    var tiles = $$('.mb-gift[data-extra]', gifts);
    if (!tiles.length) return;

    var order = [], buckets = {};
    tiles.forEach(function (t) {
      var nameEl = t.querySelector('.mb-gift__n');
      var cat = categoryOf(nameEl ? nameEl.textContent : '');
      if (!buckets[cat]) { buckets[cat] = []; order.push(cat); }
      buckets[cat].push(t);
    });

    /* One bucket is not a grouping — leave the native grid alone. */
    if (order.length < 2) return;

    /* Clear any grouping left by a previous pass BEFORE appending a new one.
       tiles[] already holds live element references, so the tiles themselves
       survive their old wrappers being removed.

       Without this the observer's own appendChild re-fires the observer, the
       second pass finds the same tiles and appends a SECOND set of category
       wrappers, and the first set is left behind as empty headings. That is
       exactly what shipped — five real categories followed by five empty ones. */
    $$('.mbfr-cat', gifts).forEach(function (el) {
      if (el.parentNode) el.parentNode.removeChild(el);
    });

    /* "More treats" last, everything else in first-appearance order. */
    order.sort(function (a, b) {
      if (a === CONFIG.OTHER) return 1;
      if (b === CONFIG.OTHER) return -1;
      return 0;
    });

    var frag = document.createDocumentFragment();
    order.forEach(function (cat) {
      var wrap = document.createElement('div');
      wrap.className = 'mbfr-cat';
      var h = document.createElement('div');
      h.className = 'mbfr-cat__h';
      h.textContent = cat;
      var rail = document.createElement('div');
      rail.className = 'mbfr-rail-x';
      var seasonal = root.getAttribute('data-mb-season') === 'halloween';
      buckets[cat].sort(function(a,b){
        var ah=/halloween/i.test(a.textContent),bh=/halloween/i.test(b.textContent);
        return seasonal ? Number(bh)-Number(ah) : Number(ah)-Number(bh);
      });
      buckets[cat].forEach(function(t,i){t.hidden=!expandedExtras[cat] && i>=3 && !t.classList.contains('is-on');rail.appendChild(t);});
      wrap.appendChild(h);
      wrap.appendChild(rail);
      if(buckets[cat].length>3){
        var more=document.createElement('button');more.type='button';more.className='mbfr-more';
        more.textContent=expandedExtras[cat]?'Show fewer':'View all '+cat.toLowerCase()+' ('+buckets[cat].length+')';
        more.setAttribute('aria-expanded',expandedExtras[cat]?'true':'false');
        more.addEventListener('click',function(){expandedExtras[cat]=!expandedExtras[cat];
          buckets[cat].forEach(function(t,i){t.hidden=!expandedExtras[cat] && i>=3 && !t.classList.contains('is-on');});
          more.setAttribute('aria-expanded',expandedExtras[cat]?'true':'false');
          more.textContent=expandedExtras[cat]?'Show fewer':'View all '+cat.toLowerCase()+' ('+buckets[cat].length+')';
        });wrap.appendChild(more);
      }
      frag.appendChild(wrap);
    });

    gifts.appendChild(frag);
    gifts.setAttribute('data-mbfr-grouped', '1');
  }

  function watchExtras() {
    var gifts = $('#mb-gifts');
    if (!gifts) return;
    /* renderExtras() does innerHTML = on every extra tap and every quantity
       bump, so the grouping is destroyed roughly once per interaction. Rebuild
       it each time, disconnecting first so our own writes don't retrigger.
       Clicks keep working throughout: mb-flow.js delegates on .mb-flow, so
       [data-extra], [data-qty] and [data-xzoom] fire wherever the tile sits. */
    var obs = new MutationObserver(function () {
      obs.disconnect();
      try { regroup(gifts); } catch (e) {}
      obs.observe(gifts, { childList: true });
    });
    obs.observe(gifts, { childList: true });
    regroup(gifts);
  }

  /* ---------- 10 · open the calendar where it can be seen ------------------
     "Choose a date" unhides #mb-calwrap below the fold, so the customer taps a
     button and nothing appears to happen — the calendar is real, it is just
     off the bottom of the window, and they have to know to scroll to find it.

     mb-flow.js does the unhiding and tells nobody, so the reveal is watched
     rather than hooked: any attribute change on #mb-calwrap that leaves it
     visible brings it into view. */

  function calendarIntoView(wrap) {
    /* The sticky footer covers the bottom of the window, so "visible" stops
       above it, not at the window edge. */
    var bar = $('.mb-bar') || $('[data-mb-bar]') || $('.mb-sticky');
    var barH = bar ? bar.getBoundingClientRect().height : 0;
    var r = wrap.getBoundingClientRect();
    var limit = window.innerHeight - barH - 12;

    if (r.bottom <= limit && r.top >= 0) return;   // already fully in view

    /* Scroll far enough to show the bottom, but never so far that the top of
       the calendar leaves the screen — a tall calendar aligns near the top
       instead, which is the best that fits. */
    var need = r.bottom - limit;
    var headroom = r.top - 80;
    var delta = Math.min(need, headroom);
    if (delta > 2) {
      window.scrollBy({ top: delta, behavior: 'smooth' });
    }
  }

  function watchCalendar() {
    var wrap = $('#mb-calwrap');
    if (!wrap) return;

    var visible = function () {
      return !wrap.hidden && wrap.getBoundingClientRect().height > 0;
    };
    var was = visible();

    var obs = new MutationObserver(function () {
      var now = visible();
      if (now && !was) {
        /* After mb-flow.js has finished writing the month in, so the height
           we measure is the real one. */
        requestAnimationFrame(function () { calendarIntoView(wrap); });
      }
      was = now;
    });
    obs.observe(wrap, { attributes: true, attributeFilter: ['hidden', 'class', 'style'] });

    /* Belt and braces for the case where the calendar was already open and
       only its contents changed — tapping "Choose a date" a second time. */
    document.addEventListener('click', function (e) {
      var opt = e.target.closest ? e.target.closest('.mb-opt') : null;
      if (!opt || !/choose a date/i.test(opt.textContent || '')) return;
      setTimeout(function () { if (visible()) calendarIntoView(wrap); }, 80);
    }, true);
  }

  /* ---------- boot -------------------------------------------------------- */

  function init() {
    styles();
    if (CONFIG.message)  messageRail();
    if (CONFIG.delivery) { watchSteps(); document.addEventListener('click', interceptNav, true); }
    if (CONFIG.extras)   watchExtras();
    if (CONFIG.calendar) watchCalendar();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();